import type { Address } from 'app/types/address';
import type { TemporaryAddressDTO } from 'app/network/address-mutations';
import { useOneGeoAddress } from 'app/network/address';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import { DEFAULT_COUNTRY } from 'app/lib/language';
import { storage, StorageKey } from 'app/lib/storage';
import type { ContactInformationFormValues } from 'app/screens/onboarding/hooks/use-contact-information-form';
import type { UseFormHandleSubmit } from 'react-hook-form';
import { useEffect, useEffectEvent, useReducer, useState } from 'react';
import type { LatLng } from 'react-native-maps';
import debounce from 'lodash/debounce';
import { BackHandler, Keyboard } from 'react-native';
import type { SearchCenter } from 'app/hooks/use-search-center';
import { preferApiEndpointForSignupCountry } from 'app/lib/axios/api-endpoint';

const hasPostalAddress = (postalCode: Address['postalCode'] | undefined, country: Address['country'] | undefined) =>
  Boolean(postalCode && country);

const getGeoCenter = (
  geoCodeAddress: { latitude: number; longitude: number } | null | undefined,
  geoCodeAddressError: unknown,
  isGeoCodeAddressFetching: boolean,
): LatLng | undefined => {
  if (!geoCodeAddress || geoCodeAddressError || isGeoCodeAddressFetching) return undefined;
  return { latitude: geoCodeAddress.latitude, longitude: geoCodeAddress.longitude };
};

const getStepVisibility = ({
  step,
  postalCodeTooLarge,
  addressCenter,
  radiusCenter,
  isGeoCodeAddressFetching,
  geoCodeAddressError,
}: {
  step: number;
  postalCodeTooLarge: boolean | undefined;
  addressCenter: LatLng | undefined;
  radiusCenter: LatLng | undefined;
  isGeoCodeAddressFetching: boolean;
  geoCodeAddressError: unknown;
}) => ({
  showMapRadius: (step === 2 || (step === 1 && !postalCodeTooLarge)) && Boolean(addressCenter) && Boolean(radiusCenter),
  showPinMap: step === 1 && Boolean(postalCodeTooLarge) && Boolean(addressCenter),
  showPostalForm: step === 0 || isGeoCodeAddressFetching,
  showChangePostal: step >= 1 && !isGeoCodeAddressFetching && !geoCodeAddressError,
});

export function useContactInformationFlow({
  handleSubmit,
  resetForm,
  showSignUpSheet,
  navigateToSignUpForm,
  isLoggedIn,
}: {
  handleSubmit: UseFormHandleSubmit<ContactInformationFormValues>;
  resetForm: (values: ContactInformationFormValues) => void;
  showSignUpSheet: () => void;
  navigateToSignUpForm: () => void;
  isLoggedIn: boolean;
}) {
  const [postalCode, setPostalCode] = useState<Address['postalCode'] | undefined>(undefined);
  const [country, setCountry] = useState<Address['country'] | undefined>(undefined);
  const [stepState, setStepState] = useState(0);
  const [radiusCenterOverride, setRadiusCenterOverride] = useState<LatLng | undefined>(undefined);
  const [addressCenterOverride, setAddressCenterOverride] = useState<LatLng | undefined>(undefined);
  const [radius, setRadius] = useState(MAX_RADIUS_IN_METERS);

  const {
    data: geoCodeAddress,
    error: geoCodeAddressError,
    isFetching: isGeoCodeAddressFetching,
  } = useOneGeoAddress(postalCode, country, {
    enabled: hasPostalAddress(postalCode, country),
  });

  const geoCenter = getGeoCenter(geoCodeAddress, geoCodeAddressError, isGeoCodeAddressFetching);
  const radiusCenter = radiusCenterOverride ?? geoCenter;
  const addressCenter = addressCenterOverride ?? geoCenter;
  const step = geoCodeAddressError ? 0 : stepState;

  // The debouncer owns timer state for this hook instance until unmount cleanup cancels it.
  const [debouncedSetAddressCenter] = useReducer(
    (current: ReturnType<typeof debounce>) => current,
    undefined,
    () => debounce(setAddressCenterOverride, 30),
  );
  useEffect(() => {
    return () => {
      debouncedSetAddressCenter.cancel();
    };
  }, [debouncedSetAddressCenter]);

  const onBackPress = () => {
    if (step === 1) {
      setPostalCode(undefined);
      setCountry(undefined);
      setAddressCenterOverride(undefined);
      setRadiusCenterOverride(undefined);
    }
    if (step > 0) {
      setStepState((s) => s - 1);
    }
  };
  const handleHardwareBackPress = useEffectEvent(() => {
    if (step > 0) {
      onBackPress();
      return true;
    }
    return false;
  });

  useEffect(() => {
    const backHandler = BackHandler.addEventListener('hardwareBackPress', handleHardwareBackPress);
    return () => backHandler.remove();
  }, []);

  const submitPostalStep = handleSubmit((form) => {
    preferApiEndpointForSignupCountry(form.country);
    setPostalCode(form.postalCode);
    setCountry(form.country);
    setStepState(1);
  });

  const doSubmitPostal = () => {
    Keyboard.dismiss();
    void submitPostalStep();
  };

  const saveMapCenter = async () => {
    if (!radiusCenter || !addressCenter) return false;
    if (geoCodeAddress?.postalCodeTooLarge && step === 1) {
      setRadiusCenterOverride({ latitude: addressCenter.latitude, longitude: addressCenter.longitude });
      setStepState((s) => s + 1);
      return false;
    }
    const payload: TemporaryAddressDTO = {
      postalCode: postalCode!,
      country: country!,
      radius,
      latitude: addressCenter.latitude,
      longitude: addressCenter.longitude,
      radiusCenterLatitude: radiusCenter.latitude === addressCenter.latitude ? null : radiusCenter.latitude,
      radiusCenterLongitude: radiusCenter.longitude === addressCenter.longitude ? null : radiusCenter.longitude,
    };
    await storage.saveString(StorageKey.TemporaryAddressUsed, JSON.stringify(payload));
    return true;
  };

  const saveThenShowSignUp = async () => {
    const saved = await saveMapCenter();
    if (saved) {
      if (isLoggedIn) {
        navigateToSignUpForm();
      } else {
        showSignUpSheet();
      }
    }
  };

  const resetToPostalInput = () => {
    setStepState(0);
    setPostalCode(undefined);
    setCountry(undefined);
    setAddressCenterOverride(undefined);
    setRadiusCenterOverride(undefined);
    setRadius(MAX_RADIUS_IN_METERS);
    resetForm({
      administrativeArea: null,
      city: null,
      country: DEFAULT_COUNTRY,
      street: null,
      unit: null,
      radiusCenterLatitude: null,
      radiusCenterLongitude: null,
      name: null,
      radius: null,
      icon: null,
      postalCode: '',
    });
  };

  const onPrimaryPress = () => {
    if (step >= 1 && !geoCodeAddressError) {
      void saveThenShowSignUp();
    } else {
      doSubmitPostal();
    }
  };

  const onMapRadiusChange = (r: number, sC: SearchCenter) => {
    setRadius(r);
    setRadiusCenterOverride({ latitude: sC.radiusCenterLatitude, longitude: sC.radiusCenterLongitude });
  };

  const { showMapRadius, showPinMap, showPostalForm, showChangePostal } = getStepVisibility({
    step,
    postalCodeTooLarge: geoCodeAddress?.postalCodeTooLarge,
    addressCenter,
    radiusCenter,
    isGeoCodeAddressFetching,
    geoCodeAddressError,
  });

  return {
    step,
    country,
    addressCenter,
    radiusCenter,
    radius,
    geoCodeAddress,
    geoCodeAddressError,
    isGeoCodeAddressFetching,
    debouncedSetAddressCenter,
    onBackPress,
    onPrimaryPress,
    onMapRadiusChange,
    resetToPostalInput,
    showMapRadius,
    showPinMap,
    showPostalForm,
    showChangePostal,
  };
}

export type ContactInformationFlowApi = ReturnType<typeof useContactInformationFlow>;
